// Bots.jsx — Phase 2: Bot Library
// List all trained AI bots for this tenant. Cards with: name, type badge,
// corpus stats, persona status, shareability. "+ New Bot" button.

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Bot, Plus, Mic2, MessageSquare, BookOpen, Clock, CheckCircle2,
  AlertCircle, Archive, Loader2, ChevronRight, Sparkles, Shield,
  Users, Globe, Lock, GitFork, RefreshCw, Share2, UserCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  draft:    { label: 'Draft',    color: 'text-white/50',   bg: 'bg-white/5',      dot: 'bg-white/40'  },
  training: { label: 'Training', color: 'text-yellow-400', bg: 'bg-yellow-400/10',dot: 'bg-yellow-400 animate-pulse' },
  ready:    { label: 'Ready',    color: 'text-civix-cyan', bg: 'bg-cyan-500/10',  dot: 'bg-civix-cyan' },
  archived: { label: 'Archived', color: 'text-white/30',   bg: 'bg-white/5',      dot: 'bg-white/20'  },
};

const SHARE_CONFIG = {
  private:           { label: 'Private',    icon: Lock,    color: 'text-white/50'   },
  downline_locked:   { label: 'Downline',   icon: Users,   color: 'text-yellow-400' },
  downline_forkable: { label: 'Forkable',   icon: GitFork, color: 'text-civix-cyan' },
  marketplace:       { label: 'Marketplace',icon: Globe,   color: 'text-civix-purple' },
};

// ─── BotCard ──────────────────────────────────────────────────────────────────
function BotCard({ bot, onOpen }) {
  const status = STATUS_CONFIG[bot.status] || STATUS_CONFIG.draft;
  const share  = SHARE_CONFIG[bot.shareability] || SHARE_CONFIG.private;
  const ShareIcon = share.icon;

  const personaReady = bot.personaProfile?.toneDescriptors?.length > 0;
  const corpusMin = Math.round(bot.trainingCorpusMinutes || 0);

  // Phase 3: ownership awareness
  const isOwner   = bot._isOwner !== false; // default true if field absent (legacy)
  const shareScope = bot._shareScope;

  return (
    <div
      onClick={() => onOpen(bot._id)}
      className="panel p-5 cursor-pointer hover:bg-white/5 transition-colors group relative"
    >
      {/* Shared-by banner (non-owners) */}
      {!isOwner && (
        <div className="absolute top-0 left-0 right-0 flex items-center gap-1.5 bg-civix-cyan/10 border-b border-civix-cyan/20 px-3 py-1.5 rounded-t-xl">
          <UserCheck className="w-3 h-3 text-civix-cyan shrink-0" />
          <span className="text-xs text-civix-cyan font-medium">
            Shared{shareScope === 'clone' ? ' (use + clone)' : ' (use only)'}
          </span>
        </div>
      )}

      {/* Header row */}
      <div className={`flex items-start justify-between gap-3 mb-4 ${!isOwner ? 'mt-7' : ''}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-civix-purple/20 flex items-center justify-center shrink-0">
            {bot.type === 'voice'
              ? <Mic2 className="w-5 h-5 text-civix-purple" />
              : <MessageSquare className="w-5 h-5 text-civix-purple" />}
          </div>
          <div>
            <div className="font-semibold text-white leading-tight">{bot.name}</div>
            {bot.description && (
              <div className="text-xs text-white/40 mt-0.5 line-clamp-1">{bot.description}</div>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-white/50 shrink-0 mt-1 transition-colors" />
      </div>

      {/* Badges row */}
      <div className="flex flex-wrap gap-2 mb-4">
        {/* Type badge */}
        <span className="px-2 py-0.5 rounded-full bg-civix-purple/20 text-civix-purple text-xs font-medium">
          {bot.type === 'voice' ? 'Voice' : 'Text'}
        </span>
        {/* Status badge */}
        <span className={`px-2 py-0.5 rounded-full ${status.bg} ${status.color} text-xs font-medium flex items-center gap-1.5`}>
          <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
          {status.label}
        </span>
        {/* Share badge — only show for owned bots */}
        {isOwner && (
          <span className={`px-2 py-0.5 rounded-full bg-white/5 ${share.color} text-xs font-medium flex items-center gap-1`}>
            <ShareIcon className="w-3 h-3" />
            {share.label}
          </span>
        )}
        {/* Read-only lock for non-owners */}
        {!isOwner && (
          <span className="px-2 py-0.5 rounded-full bg-white/5 text-white/30 text-xs font-medium flex items-center gap-1">
            <Lock className="w-3 h-3" />
            Read only
          </span>
        )}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2">
        <div className="text-center">
          <div className="text-lg font-bold text-white">{bot.trainingCorpusSize || 0}</div>
          <div className="text-xs text-white/40">Calls</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-white">{corpusMin}</div>
          <div className="text-xs text-white/40">Min Trained</div>
        </div>
        <div className="text-center">
          <div className={`text-lg font-bold ${personaReady ? 'text-civix-cyan' : 'text-white/30'}`}>
            {personaReady ? 'Yes' : 'No'}
          </div>
          <div className="text-xs text-white/40">Persona</div>
        </div>
      </div>

      {/* Last trained */}
      {bot.lastTrainedAt && (
        <div className="mt-3 pt-3 border-t border-white/5 text-xs text-white/30 flex items-center gap-1.5">
          <Clock className="w-3 h-3" />
          Last trained {new Date(bot.lastTrainedAt).toLocaleDateString()}
        </div>
      )}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
function EmptyState({ onCreate }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="w-16 h-16 rounded-2xl bg-civix-purple/20 flex items-center justify-center mb-4">
        <Bot className="w-8 h-8 text-civix-purple" />
      </div>
      <div className="text-white font-semibold text-lg mb-1">No bots yet</div>
      <div className="text-white/40 text-sm mb-6 max-w-xs">
        Create your first AI bot. Upload call recordings to train its persona and objection playbook.
      </div>
      <button
        onClick={onCreate}
        className="btn-primary flex items-center gap-2"
      >
        <Plus className="w-4 h-4" />
        New Bot
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function Bots() {
  const qc  = useQueryClient();
  const nav = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', type: 'voice' });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['bots'],
    queryFn:  () => apiGet('/bots'),
    staleTime: 10000,
  });

  const bots = data?.data || [];

  const createMutation = useMutation({
    mutationFn: (payload) => apiPost('/bots', payload),
    onSuccess: (r) => {
      const bot = r?.data || r || {};
      toast.success('Bot created!');
      setShowCreate(false);
      setForm({ name: '', description: '', type: 'voice' });
      qc.invalidateQueries({ queryKey: ['bots'] });
      nav(`/bots/${bot._id}`);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to create bot'),
  });

  const handleCreate = () => {
    if (!form.name.trim()) return toast.error('Name is required');
    createMutation.mutate(form);
  };

  return (
    <div className="min-h-screen bg-civix-dark">
      <TopBar title="Bot Library" subtitle="Train AI calling agents with your best calls" />

      <div className="p-6 max-w-6xl mx-auto">
        {/* Header action */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="text-sm text-white/40">{bots.length} bot{bots.length !== 1 ? 's' : ''}</div>
            <button onClick={() => refetch()} className="p-1.5 rounded-lg hover:bg-white/5 text-white/30 hover:text-white/60 transition-colors">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Bot
          </button>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-civix-purple animate-spin" />
          </div>
        )}

        {/* Empty */}
        {!isLoading && bots.length === 0 && (
          <EmptyState onCreate={() => setShowCreate(true)} />
        )}

        {/* Grid */}
        {!isLoading && bots.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {bots.map((bot) => (
              <BotCard key={bot._id} bot={bot} onOpen={(id) => nav(`/bots/${id}`)} />
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="panel w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-civix-purple/20 flex items-center justify-center">
                <Bot className="w-5 h-5 text-civix-purple" />
              </div>
              <div>
                <div className="font-semibold text-white">New Bot</div>
                <div className="text-xs text-white/40">Configure then train with calls</div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-white/50 mb-1.5 uppercase tracking-wider">Bot Name</label>
                <input
                  className="input w-full"
                  placeholder="e.g. Final Expense Closer"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1.5 uppercase tracking-wider">Description</label>
                <input
                  className="input w-full"
                  placeholder="Optional description"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1.5 uppercase tracking-wider">Type</label>
                <div className="flex gap-3">
                  {['voice', 'text'].map((t) => (
                    <button
                      key={t}
                      onClick={() => setForm((f) => ({ ...f, type: t }))}
                      className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                        form.type === t
                          ? 'border-civix-purple bg-civix-purple/20 text-civix-purple'
                          : 'border-white/10 bg-white/5 text-white/50 hover:bg-white/10'
                      }`}
                    >
                      {t === 'voice' ? '🎙 Voice Bot' : '💬 Text Bot'}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowCreate(false); setForm({ name: '', description: '', type: 'voice' }); }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Create Bot
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
